import type { PersistentUserSession } from '@mimir/client-common/util/persistentUserSession';
import type { ServiceCache } from '@mimir/common/client/serviceCache';
import pickBy from 'lodash/pickBy';
import { hasRouteAccess } from './routerActions';
import { initializeStore } from './store/initializeStore';
import { handleLoadingScreenRemoval } from './splash/handleLoadingScreenRemoval';
import type { StoreType } from '@mimir/web/store/state';
import * as routeUtil from '@mimir/web/util/route';
import type { AppRoutingState } from './sharedState/appRoutingState';
import type { ApplicationContext } from '@mimir/common/context/applicationContext';
import type { QuickPreview } from './model/quickPreview';
import { getDefaultFilterRedirectIfNeeded, shouldUseDefaultFilterOrBookmark } from '@mimir/web/util/filterUtils';
import { tryGetDefaultBookmarkLocation } from '@mimir/web/util/bookmarkTree';
import type { User } from '@mimir/web/model/User';
import type { RawLocation, Route } from 'vue-router/types/router';
import type { PlayerServices } from '@mimir/web/model/playerServices';
import type { ItemDetailsState } from './model/itemDetailsState';

/**
 * Sometimes we need to take the user to a different route than the one they
 * wanted. For example if they wanted a page that needs authentication, but the
 * client does not yet have any credentials.
 */

export async function getReplacementLocationIfNeeded(
  applicationContext: ApplicationContext,
  serviceCache: ServiceCache,
  session: PersistentUserSession,
  quickPreview: QuickPreview,
  itemDetailsState: ItemDetailsState,
  store: StoreType,
  appRoutingState: AppRoutingState,
  playerServices: PlayerServices,
  to: Route,
  windowLocation: Location
): Promise<RawLocation | void> {
  const result = await getReplacementLocationIfNeededWithoutStore(session, to, windowLocation);
  if (result.handled) {
    return result.location;
  }

  return getReplacementLocationIfNeededWithStore(
    applicationContext,
    serviceCache,
    session,
    quickPreview,
    itemDetailsState,
    store,
    appRoutingState,
    playerServices,
    to
  );
}

export async function getReplacementLocationIfNeededWithoutStore(session: PersistentUserSession, to: Route, windowLocation: Location): Promise<{ handled: boolean, location: RawLocation | void }> {
  if (isLegacyPath(to.path)) {
    return { handled: true, location: '/' };
  }

  if (to.path == '/') {
    const query = pickBy(to.query, isString);
    if (query.state) {
      try {
        let state: any;
        try {
          state = JSON.parse(query.state);
        } catch (_) {
          // pass
        }
        if (!state) {
          /*
            For some reason, we're getting back state where quotes are escaped
            sometimes. One place where this has been seen is when Azure Active
            Directory and Cognito integration errors out.
          */
          state = JSON.parse(query.state.replace(/\\"/g, '"'));
        }
        if (state?.pathname == '/sign-in-code') {
          const result: RawLocation = {
            path: state.pathname,
            query: { code: query.code }
          };
          return { handled: true, location: result };
        }
        const phase = await session.handleLoginInput({
          type: 'AuthenticateWithQueryParameters',
          query
        });

        if (phase?.type == 'AuthenticationCompleted') {
          return {
            handled: true,
            location: {
              path: state.pathname + state.search + state.hash,
              replace: true
            }
          };
        }
      } catch (ex) {
        console.error('getReplacementLocationIfNeeded', ex, query.state);
      }
    }
  }

  if (to.path == '/after-logout') {
    return { handled: true, location: { path: session.getAfterLogoutRedirectPath() } };
  }

  if (routeUtil.toBarePage(to) || routeUtil.toPublicPage(to) || routeUtil.toMosPage(to)) {
    // FIXME: rely on route metadata instead of this
    return { handled: true, location: undefined };
  }

  await session.initialize(windowLocation);
  if (session.loginState.refreshToken == null) {
    /*
      Allow the <App> to realize that we're not logged in, and as a result show
      a login form.
    */
    handleLoadingScreenRemoval();
    return { handled: true, location: undefined };
  }

  return { handled: false, location: undefined };
}

async function getReplacementLocationIfNeededWithStore(
  applicationContext: ApplicationContext,
  serviceCache: ServiceCache,
  session: PersistentUserSession,
  quickPreview: QuickPreview,
  itemDetailsState: ItemDetailsState,
  store: StoreType,
  appRoutingState: AppRoutingState,
  playerServices: PlayerServices,
  to: Route,
): Promise<RawLocation | void> {
  if (!appRoutingState.storeInitialized) {
    // Since we're signed in already, we can initialize the store if needed.
    await initializeStore(
      serviceCache,
      session,
      store,
      appRoutingState,
      to,
      applicationContext,
      quickPreview,
      itemDetailsState,
      playerServices,
    ).catch(ex => console.error('loadApp error', ex));
  }

  try {
    if (appRoutingState.storeInitialized) {
      // Check if user should be redirected to their primary group's default bookmark
      if (shouldUseDefaultFilterOrBookmark(to)) {
        const user = store.getters.user as User;
        const defaultBookmarkLocation = await tryGetDefaultBookmarkLocation(user);
        if (defaultBookmarkLocation) {
          return defaultBookmarkLocation;
        }
      }

      // Check if we need to apply default filters for search routes
      const defaultFilterRedirect = getDefaultFilterRedirectIfNeeded(to, store);
      if (defaultFilterRedirect) {
        return defaultFilterRedirect;
      }
      return hasRouteAccess(applicationContext, to, store) ? undefined : '/';
    }
  } catch (err) {
    console.log('router.before err', err);
  }
}


function isLegacyPath(path: string) {
  return path == '/public/login' || path == '/public/sign-up-continue';
}

function isString(val: unknown): val is string {
  return typeof val === 'string';
}

